import type { DeepPartial } from "@apollo/client/utilities";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useMemo, useState } from "react";

import { getTokenBalancesByPublicKey } from "@/app/requests";
import { getTotalEarningsRequest } from "@/backend/focus";
import { setDialog } from "@/components/app/dialogs/dialog-provider";
import { DiscoverEarningsChart } from "@/components/app/discover/discover-earnings-chart";
import {
  DiscoverFilterFields,
  TimePeriod,
} from "@/components/app/discover-sort-constants";
import { WalletBalanceTable } from "@/components/app/wallet/wallet-balance-table";
import { Skeleton } from "@/components/ui/skeleton";
import type {
  FilterNumericValueWithTimePeriod,
  FilterRecord,
} from "@/contexts/filters-sort-context";
import type { CreatorStat, TokenMetadatum } from "@/graphql/codegen/graphql";
import { useBooleanState } from "@/hooks/use-boolean-state";
import { useEnterKeyTrigger } from "@/hooks/use-handle-keydown";
import { useIdleReady } from "@/hooks/use-idle-ready";
import { APP_ROUTES, WHALE_ICON } from "@/utils/constants";
import {
  formatUSDCents,
  nanosToInteger,
  tokenNanosToInteger,
} from "@/utils/currency";
import { DIALOG } from "@/utils/dialog-routes";
import { abbreviateNumber } from "@/utils/helpers";
import { cn } from "@/utils/lib";
import { isCreatorStatType } from "@/utils/types";

interface DiscoverStatFooterProps {
  user: DeepPartial<CreatorStat | TokenMetadatum>;
  findFilterByName?: (name: DiscoverFilterFields) => FilterRecord | undefined;
  isCompact?: boolean;
}

const DiscoverCreatorTile = ({
  onClick,
  count,
  whaleCount,
  label,
  isCompact = false,
  loading = false,
}: {
  onClick?: () => void;
  count: number | string;
  whaleCount?: number | string;
  label: string;
  isCompact?: boolean;
  loading?: boolean;
}) => {
  const onKeyDown = useEnterKeyTrigger(onClick);

  const formattedCount =
    typeof count === "number" ? abbreviateNumber(count) : count;

  const formattedWhaleCount =
    typeof whaleCount === "number" ? abbreviateNumber(whaleCount) : whaleCount;

  return (
    <div
      className="
        flex cursor-pointer flex-col items-start border-b border-border-light
        p-3
        first:rounded-l-xl
        last:rounded-r-xl last:border-none
        md:border-b-0
      "
      onClick={onClick}
      onKeyDown={onKeyDown}
    >
      <p className="text-xs text-muted">{label}</p>
      {loading ? (
        <Skeleton
          className={cn(
            "h-4",
            isCompact ? "mt-0.5 w-1/2 bg-muted" : "mt-1.5 w-full",
          )}
        />
      ) : (
        <div className="flex items-end gap-3">
          <p
            className={cn("font-medium text-muted-foreground", {
              "text-lg": !isCompact,
              "text-base": Number(whaleCount?.toString()?.length) > 5,
              "text-sm": isCompact,
              "leading-4": !!whaleCount,
            })}
          >
            {formattedCount}
          </p>
          {!!whaleCount && (
            <div
              className="
                flex shrink-0 items-end gap-1 text-xs text-muted
                hover:text-muted-foreground
              "
            >
              <Image
                src={WHALE_ICON}
                unoptimized
                width={20}
                height={20}
                alt="whale-followers"
              />
              <span className="max-w-14 truncate">{formattedWhaleCount}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const DiscoverStatFooter = ({
  user,
  findFilterByName,
  isCompact = false,
}: DiscoverStatFooterProps) => {
  const {
    publicKeyBase58Check,
    username,
    totalWhaleFollowers,
    totalAssetsBalanceUsdCents: totalAssetsBalance,
    totalFocusEarningsUsdCentsAllTime,
    totalFollowers,
  } = user;

  const totalAssetsBalanceUsdCents = Number(totalAssetsBalance ?? "0");
  const [totalEarningsUsdCents, setTotalEarningsUsdCents] = useState<number>(
    totalFocusEarningsUsdCentsAllTime ?? 0,
  );

  const [desoBalanceNanos, setDesoBalanceNanos] = useState(
    isCreatorStatType(user) ? (user.desoBalanceNanos ?? 0) : 0,
  );

  const [
    assetsOpened,
    { toggle: toggleAssetsOpenedState, setFalse: hideAssetOpenState },
  ] = useBooleanState(false);
  const [
    earningOpened,
    { toggle: toggleEarningsOpenedState, setFalse: hideEarningsOpenState },
  ] = useBooleanState(false);
  const [dusdBalance, setDUSDBalance] = useState(0);
  const [focusBalance, setFocusBalance] = useState(0);
  const [focusLockedBalance, setFocusLockedBalance] = useState(0);
  const [loadingEarnings, setLoadingEarnings] = useState(false);
  const idleDiscoverFooterReady = useIdleReady(Boolean(publicKeyBase58Check), {
    timeout: 1500,
  });

  const { totalFocusPostViews, totalFocusWhalePostViews } = useMemo(() => {
    const { totalFocusPostViewsAllTime, totalFocusWhalePostViewsAllTime } =
      user;

    if (!isCreatorStatType(user)) {
      return {
        totalFocusPostViews: totalFocusPostViewsAllTime,
        totalFocusWhalePostViews: totalFocusWhalePostViewsAllTime,
      };
    }

    const {
      totalFocusPostViewsPast24H,
      totalFocusPostViewsPast30D,
      totalFocusPostViewsPast7D,
      totalFocusWhalePostViewsPast24H,
      totalFocusWhalePostViewsPast30D,
      totalFocusWhalePostViewsPast7D,
    } = user;

    const filter = findFilterByName?.(DiscoverFilterFields.totalFocusPostViews);

    const timePeriod = (filter?.value as FilterNumericValueWithTimePeriod)
      ?.timePeriod;

    switch (timePeriod) {
      case TimePeriod.Past24H:
        return {
          totalFocusPostViews: totalFocusPostViewsPast24H,
          totalFocusWhalePostViews: totalFocusWhalePostViewsPast24H,
        };
      case TimePeriod.Past7D:
        return {
          totalFocusPostViews: totalFocusPostViewsPast7D,
          totalFocusWhalePostViews: totalFocusWhalePostViewsPast7D,
        };
      case TimePeriod.Past30D:
        return {
          totalFocusPostViews: totalFocusPostViewsPast30D,
          totalFocusWhalePostViews: totalFocusWhalePostViewsPast30D,
        };

      default:
        return {
          totalFocusPostViews: totalFocusPostViewsAllTime,
          totalFocusWhalePostViews: totalFocusWhalePostViewsAllTime,
        };
    }
  }, [findFilterByName, user]);

  useEffect(() => {
    if (!publicKeyBase58Check || !idleDiscoverFooterReady) return;
    getTokenBalancesByPublicKey(publicKeyBase58Check).then((tokenBalances) => {
      if (!tokenBalances) {
        return;
      }

      const newDesoBalanceNanos =
        tokenBalances.accountWealth?.desoBalanceNanos ??
        tokenBalances.accountWealthChainUser?.desoBalanceNanos ??
        0;

      const usdBalanceBaseUnits =
        tokenBalances.accountWealth?.usdBalanceBaseUnits ??
        tokenBalances.accountWealthChainUser?.usdBalanceBaseUnits ??
        0;
      const focusTokenBalanceBaseUnits =
        tokenBalances.accountWealth?.focusTokenBalanceBaseUnits ??
        tokenBalances.accountWealthChainUser?.focusTokenBalanceBaseUnits ??
        0;
      const focusTokenLockedBalanceBaseUnits =
        tokenBalances.accountWealth?.focusTokenLockedBalanceBaseUnits ??
        tokenBalances.accountWealthChainUser
          ?.focusTokenLockedBalanceBaseUnits ??
        0;

      const dusdBalance = tokenNanosToInteger(Number(usdBalanceBaseUnits));
      const focusBalance = tokenNanosToInteger(
        Number(focusTokenBalanceBaseUnits),
      );
      const focusLockedBalance = tokenNanosToInteger(
        Number(focusTokenLockedBalanceBaseUnits),
      );

      if (desoBalanceNanos === 0) {
        setDesoBalanceNanos(newDesoBalanceNanos);
      }
      setDUSDBalance(dusdBalance);
      setFocusBalance(focusBalance);
      setFocusLockedBalance(focusLockedBalance);
    });
  }, [desoBalanceNanos, idleDiscoverFooterReady, publicKeyBase58Check]);

  useEffect(() => {
    if (!publicKeyBase58Check || !idleDiscoverFooterReady) return;

    (async () => {
      setLoadingEarnings(true);

      try {
        const data = await getTotalEarningsRequest(publicKeyBase58Check);
        setTotalEarningsUsdCents(data.EarningsUsdCents ?? 0);
      } catch (e) {
        console.error("Failed to fetch total earnings", e);
      } finally {
        setLoadingEarnings(false);
      }
    })();
  }, [idleDiscoverFooterReady, publicKeyBase58Check]);

  return (
    <div
      className={cn(
        "@container w-full flex-wrap overflow-hidden bg-clip-border",
        {
          "rounded-xl border": !isCompact,
        },
      )}
    >
      <div
        className={cn(
          `
            flex flex-wrap bg-clip-border
            *:grow
          `,
          {
            // 3 col
            "*:basis-1/3": isCompact,
            // 4 col
            "*:basis-1/2 @min-[550px]:*:basis-1/4": !isCompact,
          },
        )}
      >
        {publicKeyBase58Check && username && (
          <div
            className={cn(
              `
                unforce-radius border-r border-b border-border-light py-1
                hover:bg-card
                @min-[550px]:border-b-0
              `,
              {
                "border-b-0": isCompact,
              },
            )}
          >
            <div className="relative top-[-4px]">
              <DiscoverCreatorTile
                label="Followers"
                count={Number(totalFollowers)}
                isCompact={isCompact}
                onClick={() => {
                  hideAssetOpenState();
                  hideEarningsOpenState();
                  setDialog(DIALOG.Follow, {
                    open: true,
                    params: {
                      publicKey: publicKeyBase58Check,
                      tab: "followers",
                      username,
                    },
                  });
                }}
                whaleCount={Number(totalWhaleFollowers)}
              />
            </div>
          </div>
        )}

        {!isCompact &&
          (!!totalFocusPostViews || !!totalFocusWhalePostViews) && (
            <div
              className="
                unforce-radius border-b border-border-light
                hover:bg-card
                @min-[550px]:border-r @min-[550px]:border-b-0
              "
            >
              <DiscoverCreatorTile
                label="Post Views"
                isCompact={isCompact}
                count={Number(totalFocusPostViews)}
                whaleCount={Number(totalFocusWhalePostViews)}
              />
            </div>
          )}
        <div
          className="
            unforce-radius border-r border-border-light
            hover:bg-card
          "
        >
          <DiscoverCreatorTile
            onClick={() => {
              toggleAssetsOpenedState();
              hideEarningsOpenState();
            }}
            isCompact={isCompact}
            label="Assets"
            count={
              Number.isNaN(+totalAssetsBalanceUsdCents)
                ? "$0"
                : +totalAssetsBalanceUsdCents >= 1_000_000
                  ? `$${abbreviateNumber(+totalAssetsBalanceUsdCents / 100)}`
                  : formatUSDCents(+totalAssetsBalanceUsdCents)
            }
          />
        </div>
        <div
          className="
            unforce-radius border-none
            hover:bg-card
          "
        >
          <DiscoverCreatorTile
            onClick={() => {
              toggleEarningsOpenedState();
              hideAssetOpenState();
            }}
            isCompact={isCompact}
            label="Earnings"
            loading={loadingEarnings}
            count={
              Number.isNaN(+totalEarningsUsdCents)
                ? "$0"
                : `$${abbreviateNumber(+totalEarningsUsdCents / 100)}`
            }
          />
        </div>
      </div>

      {earningOpened && !!publicKeyBase58Check && (
        <DiscoverEarningsChart publicKey={publicKeyBase58Check} />
      )}
      {assetsOpened && !!publicKeyBase58Check && (
        <>
          <div
            className={cn("border-t border-border-light transition-all", {
              "p-4 pt-0": !isCompact,
              "p-2": isCompact,
            })}
          >
            <WalletBalanceTable
              desoBalance={nanosToInteger(desoBalanceNanos)}
              dusdBalance={dusdBalance}
              focusBalance={focusBalance}
              focusLockedBalance={focusLockedBalance}
              holderPublicKey={publicKeyBase58Check}
            />
          </div>
          <div className="border-t border-border-light p-4 transition-all">
            <div
              className="
                flex items-center justify-center text-center text-xs text-muted
                hover:text-muted-foreground
              "
            >
              <Link href={`${APP_ROUTES.wallet}/${username}`} prefetch={false}>
                View Wallet
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DiscoverStatFooter;
